import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { HotkeyHintProps } from '../../../components/menus/hotkey-hints-menu/hotkey-hint/HotkeyHint'
import { HotkeyHintsMenu } from '../../../components/menus/hotkey-hints-menu/HotkeyHintsMenu'
import { ModuleWindow } from '../../../components/moduleWindow/ModuleWindow'
import { translations } from '../../../configs/translations'
import { ClientApi } from '../../../services/clientApi'
import PlayerService from '../../../services/PlayerService'
import TrainingService from '../../../services/TrainingService'
import { useStore } from '../../../store/store'
import { NuiMessages } from '../../../utils/ClientEvents'
import './GameHudHints.css'

export interface GameHudHintsProps {
	type: "race" | "training" | "test-drive"
}

export const GameHudHints = ({ type }: GameHudHintsProps) => {

	const language = useStore(state => state.language) as keyof typeof translations
	const t = translations[language]

	const [areHintsShown, setAreHintsShown] = useState(true)
	const [hideHintsAnimationStarted, setHideHintsAnimationStarted] = useState(false)
	const [leaveModuleWindowVisible, setLeaveModuleWindowVisible] = useState(false)

	const hotkeysConfigTestDrive: HotkeyHintProps[] = [
		{
			key: 1,
			translation: t.race_hint_fix_car,
			buttons: ['1']
		},
		{
			key: 'leave',
			translation: t.freeroam_hint_leave,
			buttons: ['F3'],
		},
		{
			key: 'toggle-hints',
			translation: t.freeroam_hint_hide_menu,
			buttons: ['Shift', 'Z']
		},
	]

	const hotkeysConfigRace: HotkeyHintProps[] = [
		{
			key: 1,
			translation: t.race_hint_fix_car,
			buttons: ['1']
		},
		{
			key: 2,
			translation: t.race_hint_respawn,
			buttons: ['2']
		},
		{
			key: 3,
			translation: t.nitro,
			buttons: ['X']
		},
		{
			key: 'toggle-hints',
			translation: t.freeroam_hint_hide_menu,
			buttons: ['Shift', 'Z']
		},
	]

	const hotkeysConfigTraining: HotkeyHintProps[] = [
		{
			key: 1,
			translation: t.race_hint_fix_car,
			buttons: ['1']
		},
		{
			key: 2,
			translation: t.race_hint_respawn,
			buttons: ['2']
		},
		{
			key: 'leave',
			translation: t.freeroam_hint_leave,
			buttons: ['F3'],
		},
		{
			key: 'toggle-hints',
			translation: t.freeroam_hint_hide_menu,
			buttons: ['Shift', 'Z']
		},
	]

	useEffect(() => {
		ClientApi.on(NuiMessages.RACE_HINT, ({ id }: { id: string }) => {
			if (id === 'toggle-hints') {
				setHideHintsAnimationStarted(prev => !prev)
				setTimeout(() => setAreHintsShown(prev => !prev), 450)
			}
		})

		ClientApi.on(NuiMessages.TRAINING_HINT, ({ id }: { id: string }) => {
			if (id === 'leave' && type === 'training') {
				setLeaveModuleWindowVisible(true)
				toggleCursor(true)
			}
		})
	}, [])

	const handleTrainingLeave = () => {
		TrainingService.leave()
			.catch((error) => {
				toast.error(
					error?.response?.data && t[error.response.data as keyof typeof t]
						? t[error.response.data as keyof typeof t]
						: t.error_unknown,
					{ className: 'notification-error' }
				)
			})
	}

	useCallback(() => {
		const sound = new Audio(
			areHintsShown
				? './sounds/woosh_1.wav'
				: './sounds/woosh_1_reversed.wav'
		)

		sound.volume = 0.01
		void sound.play().catch(() => undefined)

		if (areHintsShown) {
			setHideHintsAnimationStarted(true)

			window.setTimeout(() => {
				setAreHintsShown(false)
				setHideHintsAnimationStarted(false)
			}, 450)

			return
		}

		setAreHintsShown(true)
	}, [areHintsShown])

	const getActualConfig = () => {
		switch (type) {
			case 'race':
				return hotkeysConfigRace
			case 'training':
				return hotkeysConfigTraining
			default:
				return hotkeysConfigTestDrive
		}
	}

	const toggleCursor = (enabled: boolean) => {
		try {
			console.log(`Toggle cursor ${enabled}`)
			enabled ? PlayerService.showCursor() : PlayerService.hideCursor()
		} catch (error) {
			console.log('Unable to toggle cursor, error:', error)
		}
	}

	return (
		<>
			{areHintsShown && (
				<div className={`game-hud-hints-menu-wrapper ${hideHintsAnimationStarted ? 'slide-out' : ''}`}>
					<HotkeyHintsMenu hints={getActualConfig()} />
				</div>
			)}

			{leaveModuleWindowVisible && <ModuleWindow
				title={t.leave}
				text={t.are_you_sure_leave_freeroam}
				onYes={() => {
					void handleTrainingLeave()
					setLeaveModuleWindowVisible(false)
				}}
				onNo={() => {
					setLeaveModuleWindowVisible(false)
					void toggleCursor(false)
				}}
			/>}
		</>
	)
}